import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { withUnidade } from '../../utils/storageByUnit';

export default function GavConfigPanel({ setSnackbar }) {
  const { unidadeSelecionada } = useAuth();
  const [editais, setEditais] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [formEdital, setFormEdital] = useState({
    codigo: '',
    titulo: '',
    processoId: '',
    dataAbertura: '',
    dataEncerramento: '',
    descricao: '',
    chamamento: '1'
  });
  const [editSolic, setEditSolic] = useState({ open: false, row: null, email: '', senha: '' });

  const u = (path) => withUnidade(path, unidadeSelecionada);

  const carregar = async () => {
    try {
      const [rEd, rSol, rProc] = await Promise.all([
        apiFetch(u('/api/admin/editais')),
        apiFetch(u('/api/admin/solicitacoes-cadastro-prestador')),
        apiFetch(u('/api/admin/processos-contratacao'))
      ]);
      if (rEd.ok) setEditais(await rEd.json());
      if (rSol.ok) setSolicitacoes(await rSol.json());
      if (rProc.ok) setProcessos(await rProc.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadeSelecionada?.codigo]);

  const criarEdital = async () => {
    try {
      const payload = { ...formEdital, processoId: formEdital.processoId || null };
      const res = await apiFetch(u('/api/admin/editais'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao criar edital');
      setSnackbar({ open: true, message: 'Edital criado.', severity: 'success' });
      setFormEdital({
        codigo: '',
        titulo: '',
        processoId: '',
        dataAbertura: '',
        dataEncerramento: '',
        descricao: '',
        chamamento: '1'
      });
      carregar();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const cancelarEdital = async (id) => {
    if (!window.confirm('Cancelar este edital? (soft delete — não apaga o registro)')) return;
    try {
      const res = await apiFetch(u(`/api/admin/editais/${id}/cancelar`), { method: 'PATCH' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao cancelar');
      setSnackbar({ open: true, message: data.message || 'Edital cancelado.', severity: 'success' });
      carregar();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const aprovar = async (id) => {
    const senhaInicial = window.prompt('Senha inicial para o prestador (deixe vazio para 123456):', '123456');
    try {
      const res = await apiFetch(u(`/api/admin/solicitacoes-cadastro-prestador/${id}/aprovar`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senhaInicial: senhaInicial || '123456' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao aprovar');
      setSnackbar({
        open: true,
        message: data.message || `Aprovado. Senha inicial: ${data.senhaInicial}`,
        severity: 'success'
      });
      carregar();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const rejeitar = async (id) => {
    await apiFetch(u(`/api/admin/solicitacoes-cadastro-prestador/${id}/rejeitar`), { method: 'POST' });
    carregar();
  };

  const abrirEditarSolic = (row) => {
    setEditSolic({ open: true, row, email: row.email || '', senha: '' });
  };

  const salvarEditarSolic = async () => {
    try {
      const res = await apiFetch(u(`/api/admin/solicitacoes-cadastro-prestador/${editSolic.row.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: editSolic.email,
          senha: editSolic.senha || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setSnackbar({ open: true, message: 'E-mail/senha atualizados.', severity: 'success' });
      setEditSolic({ open: false, row: null, email: '', senha: '' });
      carregar();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const inativarSolic = async (id) => {
    if (!window.confirm('Inativar este cadastro de prestador? (soft delete)')) return;
    try {
      const res = await apiFetch(u(`/api/admin/solicitacoes-cadastro-prestador/${id}/inativar`), {
        method: 'PATCH'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao inativar');
      setSnackbar({ open: true, message: data.message || 'Inativado.', severity: 'success' });
      carregar();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Novo edital</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Código"
            size="small"
            fullWidth
            value={formEdital.codigo}
            onChange={(e) => setFormEdital((p) => ({ ...p, codigo: e.target.value.toUpperCase() }))}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            label="Título"
            size="small"
            fullWidth
            value={formEdital.titulo}
            onChange={(e) => setFormEdital((p) => ({ ...p, titulo: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Processo de contratação</InputLabel>
            <Select
              label="Processo de contratação"
              value={formEdital.processoId}
              onChange={(e) => setFormEdital((p) => ({ ...p, processoId: e.target.value }))}
            >
              <MenuItem value=""><em>Sem vínculo</em></MenuItem>
              {processos.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.codigo} — {p.titulo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            label="Abertura"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formEdital.dataAbertura}
            onChange={(e) => setFormEdital((p) => ({ ...p, dataAbertura: e.target.value }))}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            label="Encerramento"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formEdital.dataEncerramento}
            onChange={(e) => setFormEdital((p) => ({ ...p, dataEncerramento: e.target.value }))}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            label="Chamamento nº"
            type="number"
            size="small"
            fullWidth
            value={formEdital.chamamento}
            onChange={(e) => setFormEdital((p) => ({ ...p, chamamento: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={criarEdital} sx={{ textTransform: 'none' }}>
            Cadastrar edital
          </Button>
        </Grid>
      </Grid>

      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Código</TableCell>
            <TableCell>Título</TableCell>
            <TableCell>Processo</TableCell>
            <TableCell>Encerramento</TableCell>
            <TableCell>Ativo</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {editais.map((e) => (
            <TableRow key={e.id}>
              <TableCell>{e.codigo}</TableCell>
              <TableCell>{e.titulo}</TableCell>
              <TableCell>
                {e.processo_codigo
                  ? `${e.processo_codigo}${e.processo_titulo ? ` — ${e.processo_titulo}` : ''}`
                  : '—'}
              </TableCell>
              <TableCell>{e.data_encerramento || '—'}</TableCell>
              <TableCell>{e.ativo ? 'Sim' : 'Não'}</TableCell>
              <TableCell align="right">
                <Tooltip title="Cancelar edital">
                  <IconButton color="error" size="small" onClick={() => cancelarEdital(e.id)}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {editais.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">Nenhum edital ativo.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Solicitações de cadastro (prestadores) — unidade {unidadeSelecionada?.codigo || 'atual'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Cada unidade vê apenas as próprias solicitações.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Razão social</TableCell>
            <TableCell>CNPJ</TableCell>
            <TableCell>E-mail</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {solicitacoes.map((s) => (
            <TableRow key={s.id}>
              <TableCell>{s.razao_social}</TableCell>
              <TableCell>{s.cnpj}</TableCell>
              <TableCell>{s.email || '—'}</TableCell>
              <TableCell><Chip size="small" label={s.status} /></TableCell>
              <TableCell align="right">
                {s.status === 'PENDENTE' && (
                  <>
                    <Button size="small" onClick={() => aprovar(s.id)} sx={{ textTransform: 'none' }}>Aprovar</Button>
                    <Button size="small" color="error" onClick={() => rejeitar(s.id)} sx={{ textTransform: 'none' }}>Rejeitar</Button>
                  </>
                )}
                <Tooltip title="Editar e-mail e senha">
                  <IconButton size="small" color="primary" onClick={() => abrirEditarSolic(s)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Inativar (soft delete)">
                  <IconButton size="small" color="error" onClick={() => inativarSolic(s.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {solicitacoes.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">Nenhuma solicitação.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={editSolic.open} onClose={() => setEditSolic({ open: false, row: null, email: '', senha: '' })} fullWidth maxWidth="xs">
        <DialogTitle>Editar acesso do prestador</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editSolic.row?.razao_social}
          </Typography>
          <TextField
            label="E-mail"
            fullWidth
            sx={{ mb: 2 }}
            value={editSolic.email}
            onChange={(e) => setEditSolic((p) => ({ ...p, email: e.target.value }))}
          />
          <TextField
            label="Nova senha"
            type="password"
            fullWidth
            helperText="Deixe em branco para não alterar a senha"
            value={editSolic.senha}
            onChange={(e) => setEditSolic((p) => ({ ...p, senha: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSolic({ open: false, row: null, email: '', senha: '' })}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEditarSolic}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
